import { ChevronRightIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { formatAppName } from '../lib/appName';
import { formatTimestamp } from '../lib/history';
import {
  bumpVersionTag,
  getInitialVersionTag,
  isVersionTag,
  type VersionBump,
} from '../lib/version';
import type {
  Deployment,
  DeploymentQueueItem,
  GithubDeploymentAuthItem,
  GithubDeploymentAuthResult,
  InfrastructureApp,
  VerificationResult,
} from '../types';
import { CopyableVersionTag } from './CopyableVersionTag';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

type VersionMode = VersionBump | 'custom';
type WizardStep = 'versions' | 'review';

interface VersionSelection {
  mode: VersionMode;
  customVersion: string;
}

interface DeploymentVersionWizardProps {
  open: boolean;
  items: DeploymentQueueItem[];
  apps: InfrastructureApp[];
  deployments: Deployment[];
  verificationResults: VerificationResult[];
  initialStep?: WizardStep;
  githubAuthStatus?: 'idle' | 'validating' | 'validated' | 'error';
  githubAuthResult?: GithubDeploymentAuthResult;
  githubAuthError?: string;
  onRemove: (appName: string, environment: string) => void;
  onStartGithubAuth: (deployments: GithubDeploymentAuthItem[]) => void;
  onOpenChange: (open: boolean) => void;
}

const versionModes: Array<{ label: string; value: VersionMode }> = [
  { label: 'Bugfix', value: 'bugfix' },
  { label: 'Minor', value: 'minor' },
  { label: 'Major', value: 'major' },
  { label: 'Custom', value: 'custom' },
];

const bumpModes: Array<{ label: string; value: VersionBump }> = [
  { label: 'Bugfix', value: 'bugfix' },
  { label: 'Minor', value: 'minor' },
  { label: 'Major', value: 'major' },
];

const wizardGridColumns = 'grid-cols-[minmax(180px,1.2fr)_64px_84px_minmax(244px,1fr)_84px_28px]';
const badgeClassName = 'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium leading-4';

const getItemKey = (item: DeploymentQueueItem) => `${item.appName}:${item.environment}`;

const getLatestDeployment = (
  deployments: Deployment[],
  appName: string,
  environment: string,
): Deployment | undefined =>
  deployments
    .filter(
      (deployment) => deployment.appName === appName && deployment.environment === environment,
    )
    .sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    )[0];

const getVerification = (
  verificationResults: VerificationResult[],
  deployment: Deployment | undefined,
): VerificationResult | undefined =>
  deployment
    ? verificationResults.find((verification) => verification.deploymentId === deployment.id)
    : undefined;

const getStatusColorClass = (status: string) => {
  switch (status) {
    case 'success':
      return 'bg-green-100 text-green-800';
    case 'failure':
    case 'error':
      return 'bg-red-100 text-red-800';
    case 'in_progress':
    case 'pending':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export const DeploymentVersionWizard = ({
  open,
  items,
  apps,
  deployments,
  verificationResults,
  initialStep = 'versions',
  githubAuthStatus = 'idle',
  githubAuthResult,
  githubAuthError,
  onRemove,
  onStartGithubAuth,
  onOpenChange,
}: DeploymentVersionWizardProps) => {
  const sortedItems = useMemo(
    () =>
      [...items].sort((left, right) => {
        const appNameComparison = formatAppName(left.appName).localeCompare(
          formatAppName(right.appName),
        );

        if (appNameComparison !== 0) {
          return appNameComparison;
        }

        return left.environment.localeCompare(right.environment);
      }),
    [items],
  );

  const latestDeploymentsByItem = useMemo(() => {
    const nextMap = new Map<string, Deployment | undefined>();

    sortedItems.forEach((item) => {
      nextMap.set(
        getItemKey(item),
        getLatestDeployment(deployments, item.appName, item.environment),
      );
    });

    return nextMap;
  }, [deployments, sortedItems]);

  const isSensitiveDeployment = (item: DeploymentQueueItem) =>
    apps
      .find((app) => app.name === item.appName)
      ?.bulkDeploymentExclusions.includes(item.environment) ?? false;

  const [selections, setSelections] = useState<Record<string, VersionSelection>>({});
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [step, setStep] = useState<WizardStep>('versions');

  useEffect(() => {
    if (!open) {
      setSelections({});
      setExpandedItems(new Set());
      setStep('versions');
      return;
    }

    if (sortedItems.length === 0 && githubAuthStatus !== 'error' && !githubAuthError) {
      onOpenChange(false);
      return;
    }

    const nextSelections: Record<string, VersionSelection> = {};

    sortedItems.forEach((item) => {
      const key = getItemKey(item);
      const previousVersion = latestDeploymentsByItem.get(key)?.version;
      const queuedVersion =
        initialStep === 'review' && item.version && isVersionTag(item.version)
          ? item.version
          : undefined;

      nextSelections[key] = {
        mode: queuedVersion ? 'custom' : 'bugfix',
        customVersion: queuedVersion
          ? queuedVersion
          : previousVersion
            ? (bumpVersionTag(previousVersion, 'bugfix') ?? '')
            : getInitialVersionTag('bugfix'),
      };
    });

    setSelections(nextSelections);
    setStep(initialStep);
  }, [githubAuthError, githubAuthStatus, initialStep, latestDeploymentsByItem, open, sortedItems]);

  const getResultVersion = (item: DeploymentQueueItem): string | undefined => {
    const key = getItemKey(item);
    const selection = selections[key];

    if (!selection) {
      return undefined;
    }

    if (selection.mode === 'custom') {
      return selection.customVersion.trim();
    }

    const previousVersion = latestDeploymentsByItem.get(key)?.version;

    return previousVersion
      ? bumpVersionTag(previousVersion, selection.mode)
      : getInitialVersionTag(selection.mode);
  };

  const setMode = (item: DeploymentQueueItem, mode: VersionMode) => {
    const key = getItemKey(item);
    const previousVersion = latestDeploymentsByItem.get(key)?.version;

    setSelections((current) => ({
      ...current,
      [key]: {
        mode,
        customVersion:
          mode === 'custom'
            ? (current[key]?.customVersion ?? '')
            : previousVersion
              ? (bumpVersionTag(previousVersion, mode) ?? '')
              : getInitialVersionTag(mode),
      },
    }));
  };

  const setCustomVersion = (item: DeploymentQueueItem, customVersion: string) => {
    const key = getItemKey(item);

    setSelections((current) => ({
      ...current,
      [key]: {
        mode: 'custom',
        customVersion,
      },
    }));
  };

  const setAllModes = (mode: VersionBump) => {
    setSelections(() => {
      const nextSelections: Record<string, VersionSelection> = {};

      sortedItems.forEach((item) => {
        const key = getItemKey(item);
        const previousVersion = latestDeploymentsByItem.get(key)?.version;

        nextSelections[key] = {
          mode,
          customVersion: previousVersion
            ? (bumpVersionTag(previousVersion, mode) ?? '')
            : getInitialVersionTag(mode),
        };
      });

      return nextSelections;
    });
  };

  const toggleExpanded = (key: string) => {
    setExpandedItems((current) => {
      const nextExpandedItems = new Set(current);

      if (nextExpandedItems.has(key)) {
        nextExpandedItems.delete(key);
      } else {
        nextExpandedItems.add(key);
      }

      return nextExpandedItems;
    });
  };

  const renderDeploymentDetails = (
    deployment: Deployment | undefined,
    verification: VerificationResult | undefined,
    label: string,
  ) => (
    <div className='rounded-md border border-gray-200 bg-white px-3 py-2'>
      <div className='mb-2 text-xs font-medium uppercase text-gray-500'>{label}</div>
      {deployment ? (
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between gap-3'>
            <span className='text-xs text-gray-500'>Version</span>
            <CopyableVersionTag version={deployment.version} />
          </div>
          <div className='flex items-center justify-between gap-3'>
            <span className='text-xs text-gray-500'>Deployed</span>
            <span className='text-right text-xs text-gray-900'>
              {formatTimestamp(deployment.createdAt)}
            </span>
          </div>
          <div className='flex items-center justify-between gap-3'>
            <span className='text-xs text-gray-500'>Status</span>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${getStatusColorClass(
                deployment.status,
              )}`}
            >
              {deployment.status}
            </span>
          </div>
          <div className='border-t border-gray-100 pt-1.5'>
            <div className='flex items-center justify-between gap-3'>
              <span className='text-xs text-gray-500'>Validation</span>
              {verification ? (
                <span className='rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800'>
                  validated
                </span>
              ) : (
                <span className='rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'>
                  missing
                </span>
              )}
            </div>
            {verification && (
              <div className='mt-1.5 flex items-center justify-between gap-3'>
                <span className='text-xs text-gray-500'>Validated</span>
                <span className='text-right text-xs text-gray-900'>
                  {formatTimestamp(verification.createdAt)}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className='text-sm text-gray-500'>No deployment found</div>
      )}
    </div>
  );

  const allItemsInitialized = sortedItems.every((item) => !!selections[getItemKey(item)]);
  const invalidItems = allItemsInitialized
    ? sortedItems.filter((item) => {
        const resultVersion = getResultVersion(item);

        return !resultVersion || !isVersionTag(resultVersion);
      })
    : [];
  const reviewedDeployments = sortedItems
    .map((item) => ({
      item,
      key: getItemKey(item),
      latestDeployment: latestDeploymentsByItem.get(getItemKey(item)),
      resultVersion: getResultVersion(item),
      sensitive: isSensitiveDeployment(item),
    }))
    .filter((deployment) => deployment.resultVersion);
  const githubDeploymentAuthItems: GithubDeploymentAuthItem[] = reviewedDeployments.map(
    ({ item, resultVersion }) => ({
      appName: item.appName,
      environment: item.environment,
      version: resultVersion ?? '',
    }),
  );
  const hasAuthErrorWithoutQueue =
    sortedItems.length === 0 && (githubAuthStatus === 'error' || !!githubAuthError);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[min(960px,calc(100vw-2rem))] max-w-none'>
        <DialogHeader>
          <DialogTitle>
            {step === 'versions' ? 'Select deployment versions' : 'Review GitHub deployment'}
          </DialogTitle>
          <DialogDescription>
            {step === 'versions'
              ? 'Choose how each queued deployment should update its current version tag.'
              : 'Confirm the workflow runs that will be triggered after GitHub authentication.'}
          </DialogDescription>
        </DialogHeader>

        {hasAuthErrorWithoutQueue ? (
          <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
            {githubAuthError ?? 'GitHub authentication could not be validated.'}
          </div>
        ) : step === 'versions' ? (
          <div className='space-y-4'>
            <div className='overflow-x-auto rounded-md border border-gray-200'>
              <div className='min-w-[760px]'>
                <div
                  className={`grid ${wizardGridColumns} gap-3 border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-500`}
                >
                  <div>App</div>
                  <div>Env</div>
                  <div>Current</div>
                  <div>Bump</div>
                  <div>Result</div>
                  <div />
                </div>
                <div
                  className={`grid ${wizardGridColumns} items-center gap-3 border-b border-gray-100 bg-gray-50 px-3 py-2`}
                >
                  <div className='col-span-3 text-xs text-gray-500'>
                    Apply to all queued targets
                  </div>
                  <div className='flex items-center gap-1.5'>
                    {bumpModes.map((mode) => (
                      <Button
                        key={mode.value}
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => setAllModes(mode.value)}
                        className='h-7 px-2 text-xs'
                      >
                        {mode.label}
                      </Button>
                    ))}
                  </div>
                  <div />
                  <div />
                </div>
                <div className='max-h-[45vh] overflow-y-auto'>
                  {sortedItems.map((item) => {
                    const key = getItemKey(item);
                    const selection = selections[key];
                    const latestDeployment = latestDeploymentsByItem.get(key);
                    const latestDevDeployment = getLatestDeployment(
                      deployments,
                      item.appName,
                      'dev',
                    );
                    const latestProdDeployment = getLatestDeployment(
                      deployments,
                      item.appName,
                      'prod',
                    );
                    const latestDevVerification = getVerification(
                      verificationResults,
                      latestDevDeployment,
                    );
                    const latestProdVerification = getVerification(
                      verificationResults,
                      latestProdDeployment,
                    );
                    const resultVersion = getResultVersion(item);
                    const resultIsValid = !!resultVersion && isVersionTag(resultVersion);
                    const expanded = expandedItems.has(key);
                    const sensitive = isSensitiveDeployment(item);
                    const envColorClass =
                      item.environment === 'prod'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800';

                    return (
                      <div key={key} className='border-b border-gray-100 last:border-b-0'>
                        <div
                          role='button'
                          tabIndex={0}
                          onClick={(event) => {
                            if ((event.target as HTMLElement).closest('button,input')) {
                              return;
                            }

                            toggleExpanded(key);
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter' && event.key !== ' ') {
                              return;
                            }

                            event.preventDefault();
                            toggleExpanded(key);
                          }}
                          className={`grid cursor-pointer ${wizardGridColumns} items-center gap-3 px-3 py-3 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring`}
                          title={expanded ? 'Hide deployment details' : 'Show deployment details'}
                        >
                          <div className='flex min-w-0 items-center gap-2'>
                            <ChevronRightIcon
                              className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${
                                expanded ? 'rotate-90' : ''
                              }`}
                            />
                            <div className='truncate text-sm font-medium text-gray-900'>
                              {formatAppName(item.appName)}
                            </div>
                            {sensitive && (
                              <span
                                className={`${badgeClassName} shrink-0 bg-secondary text-secondary-foreground`}
                                title='Excluded from bulk deployment actions'
                              >
                                Sensitive
                              </span>
                            )}
                          </div>
                          <div className='flex items-center'>
                            <span className={`${badgeClassName} ${envColorClass}`}>
                              {item.environment}
                            </span>
                          </div>
                          <div>
                            {latestDeployment ? (
                              <CopyableVersionTag version={latestDeployment.version} />
                            ) : (
                              <span className='text-xs text-gray-500'>new</span>
                            )}
                          </div>
                          <div className='flex min-w-0 items-center gap-1.5'>
                            {versionModes.map((mode) => (
                              <button
                                key={mode.value}
                                type='button'
                                onClick={() => setMode(item, mode.value)}
                                className={`rounded border px-2 py-1 text-xs font-medium transition-colors ${
                                  selection?.mode === mode.value
                                    ? 'border-primary bg-primary text-primary-foreground'
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {mode.label}
                              </button>
                            ))}
                          </div>
                          <div>
                            {selection?.mode === 'custom' ? (
                              <input
                                value={selection.customVersion}
                                onChange={(event) => setCustomVersion(item, event.target.value)}
                                placeholder='v1.2.3'
                                className={`h-8 w-full rounded-md border px-2 font-mono text-sm outline-none focus:ring-2 focus:ring-ring ${
                                  selection.customVersion && !resultIsValid
                                    ? 'border-red-300 text-red-700'
                                    : 'border-gray-200 text-gray-900'
                                }`}
                              />
                            ) : resultVersion ? (
                              <CopyableVersionTag version={resultVersion} />
                            ) : (
                              <span className='text-xs text-gray-500'>initial tag</span>
                            )}
                          </div>
                          <button
                            type='button'
                            onClick={(event) => {
                              event.stopPropagation();
                              onRemove(item.appName, item.environment);
                            }}
                            className='rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            aria-label={`Remove ${formatAppName(item.appName)} ${item.environment} from deployment batch`}
                            title='Remove from batch'
                          >
                            <XMarkIcon className='h-4 w-4' />
                          </button>
                        </div>
                        <div
                          className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                            expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                          }`}
                        >
                          <div className='min-h-0 overflow-hidden'>
                            <div
                              className={`space-y-3 bg-gray-50 px-3 pb-3 pt-1 transition-opacity duration-150 ${
                                expanded ? 'opacity-100 delay-75' : 'opacity-0'
                              }`}
                            >
                              {sensitive && (
                                <div className='flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
                                  <ExclamationTriangleIcon className='mt-0.5 h-4 w-4 shrink-0' />
                                  <div>
                                    This target is excluded from bulk deployment actions and should
                                    be reviewed carefully before deploying.
                                  </div>
                                </div>
                              )}
                              <div className='grid grid-cols-2 gap-3'>
                                {renderDeploymentDetails(
                                  latestDevDeployment,
                                  latestDevVerification,
                                  'Dev deployment',
                                )}
                                {renderDeploymentDetails(
                                  latestProdDeployment,
                                  latestProdVerification,
                                  'Prod deployment',
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {invalidItems.length > 0 && (
              <div className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
                Add a valid custom tag like v1.2.3 for {invalidItems.length} queued target
                {invalidItems.length === 1 ? '' : 's'}.
              </div>
            )}
          </div>
        ) : (
          <div className='space-y-4'>
            <div className='grid grid-cols-3 gap-3'>
              <div className='rounded-md border border-gray-200 bg-gray-50 px-3 py-2'>
                <div className='text-xs font-medium uppercase text-gray-500'>Workflow runs</div>
                <div className='mt-1 text-2xl font-semibold text-gray-900'>
                  {reviewedDeployments.length}
                </div>
              </div>
              <div className='rounded-md border border-gray-200 bg-gray-50 px-3 py-2'>
                <div className='text-xs font-medium uppercase text-gray-500'>Dev</div>
                <div className='mt-1 text-2xl font-semibold text-gray-900'>
                  {
                    reviewedDeployments.filter(
                      (deployment) => deployment.item.environment === 'dev',
                    ).length
                  }
                </div>
              </div>
              <div className='rounded-md border border-gray-200 bg-gray-50 px-3 py-2'>
                <div className='text-xs font-medium uppercase text-gray-500'>Prod</div>
                <div className='mt-1 text-2xl font-semibold text-gray-900'>
                  {
                    reviewedDeployments.filter(
                      (deployment) => deployment.item.environment === 'prod',
                    ).length
                  }
                </div>
              </div>
            </div>

            <div className='overflow-hidden rounded-md border border-gray-200'>
              <div className='grid grid-cols-[minmax(180px,1fr)_72px_100px_100px] gap-3 border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-500'>
                <div>App</div>
                <div>Env</div>
                <div>Current</div>
                <div>Target</div>
              </div>
              <div className='max-h-[45vh] overflow-y-auto'>
                {reviewedDeployments.map(
                  ({ item, key, latestDeployment, resultVersion, sensitive }) => {
                    const envColorClass =
                      item.environment === 'prod'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800';

                    return (
                      <div
                        key={key}
                        className='grid grid-cols-[minmax(180px,1fr)_72px_100px_100px] items-center gap-3 border-b border-gray-100 px-3 py-3 last:border-b-0'
                      >
                        <div className='flex min-w-0 items-center gap-2'>
                          <div className='truncate text-sm font-medium text-gray-900'>
                            {formatAppName(item.appName)}
                          </div>
                          {sensitive && (
                            <span
                              className={`${badgeClassName} shrink-0 bg-secondary text-secondary-foreground`}
                              title='Excluded from bulk deployment actions'
                            >
                              Sensitive
                            </span>
                          )}
                        </div>
                        <div>
                          <span className={`${badgeClassName} ${envColorClass}`}>
                            {item.environment}
                          </span>
                        </div>
                        <div>
                          {latestDeployment ? (
                            <CopyableVersionTag version={latestDeployment.version} />
                          ) : (
                            <span className='text-xs text-gray-500'>new</span>
                          )}
                        </div>
                        <div>
                          <CopyableVersionTag version={resultVersion ?? ''} />
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>

            {githubAuthStatus === 'validating' && (
              <div className='rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800'>
                Validating GitHub authentication...
              </div>
            )}
            {githubAuthStatus === 'validated' && githubAuthResult && (
              <div
                className={`rounded-md border px-3 py-2 text-sm ${
                  githubAuthResult.failedDeployments > 0
                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                    : 'border-green-200 bg-green-50 text-green-800'
                }`}
              >
                GitHub accepted {githubAuthResult.acceptedDeployments} of{' '}
                {githubAuthResult.requestedDeployments} workflow run
                {githubAuthResult.requestedDeployments === 1 ? '' : 's'} for{' '}
                {githubAuthResult.githubUser.login}.
              </div>
            )}
            {githubAuthStatus === 'validated' &&
              githubAuthResult &&
              githubAuthResult.failedDeployments > 0 && (
                <div className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
                  <div className='font-medium'>
                    {githubAuthResult.failedDeployments} workflow dispatch
                    {githubAuthResult.failedDeployments === 1 ? '' : 'es'} failed.
                  </div>
                  <div className='mt-1 space-y-1'>
                    {githubAuthResult.dispatches
                      .filter((dispatch) => dispatch.status === 'failed')
                      .map((dispatch) => (
                        <div key={`${dispatch.appName}:${dispatch.environment}`}>
                          {formatAppName(dispatch.appName)} {dispatch.environment}:{' '}
                          {dispatch.error ?? 'GitHub rejected the workflow dispatch.'}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            {githubAuthStatus === 'error' && (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                {githubAuthError ?? 'GitHub authentication could not be validated.'}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => {
              if (hasAuthErrorWithoutQueue) {
                onOpenChange(false);
                return;
              }

              if (step === 'review') {
                setStep('versions');
                return;
              }

              onOpenChange(false);
            }}
          >
            {step === 'review' ? 'Back' : 'Back'}
          </Button>
          {hasAuthErrorWithoutQueue ? null : step === 'versions' ? (
            <Button
              disabled={hasAuthErrorWithoutQueue || !allItemsInitialized || invalidItems.length > 0}
              onClick={() => setStep('review')}
              title={
                invalidItems.length > 0
                  ? 'Fix invalid version tags before continuing'
                  : 'Review GitHub workflow runs'
              }
            >
              Continue
            </Button>
          ) : (
            <Button
              disabled={
                githubAuthStatus === 'validating' ||
                (githubAuthStatus === 'validated' &&
                  (githubAuthResult?.failedDeployments ?? 0) === 0)
              }
              onClick={() => {
                const failedItems =
                  githubAuthStatus === 'validated' && githubAuthResult?.failedDeployments
                    ? githubAuthResult.dispatches
                        .filter((dispatch) => dispatch.status === 'failed')
                        .map((dispatch) => ({
                          appName: dispatch.appName,
                          environment: dispatch.environment,
                          version: dispatch.version,
                        }))
                    : [];

                onStartGithubAuth(failedItems.length > 0 ? failedItems : githubDeploymentAuthItems);
              }}
              title='Authenticate with GitHub and trigger deployment workflows'
            >
              {githubAuthStatus === 'validated' && (githubAuthResult?.failedDeployments ?? 0) === 0
                ? 'Deployment Triggered'
                : githubAuthStatus === 'validated'
                  ? 'Retry Failed Dispatches'
                  : 'Authenticate and Deploy'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
